import { format, subDays } from 'date-fns';
import { useEffect } from 'react';
import { useSWRConfig } from 'swr';
import { useShallow } from 'zustand/react/shallow';

import { authProvidersOptions as fetchAuthProvidersOptions } from '@/api/auth';
import { MethodGroupType } from '@/api/cms';
import { playerFields as fetchPlayerFields } from '@/api/common';
import { playerSettings as fetchPlayerSettings } from '@/api/common';
import { TransactionState, userLimits as fetchUserLimits } from '@/api/profile';
import { fetchToStore } from '@/helpers/fetchers';
import { groupMemberNoDeposits } from '@/helpers/user';
import {
  InvreasedPrevDepEnum,
  playerPaymentsWithPages as fetchPlayerPaymentsWithPages,
} from '@/modules/deposit';
import { tournamentGame as fetchTournamentGame } from '@/modules/tournaments/api';
import { useCommonStore, useLocalStorageStore } from '@/store';

export const useInitStore = () => {
  const { mutate } = useSWRConfig();

  const {
    isAuthenticated,
    pendingUserPure,
    user,
    currentWallet,
    lastSuccessfulPaymentsByCurrency,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      pendingUserPure: state.pendingUserPure,
      user: state.user,
      currentWallet: state.currentWallet,
      lastSuccessfulPaymentsByCurrency: state.lastSuccessfulPaymentsByCurrency,
    }))
  );

  useEffect(() => {
    try {
      fetchToStore(fetchPlayerFields, 'playerFields');
      fetchTournamentGame().then(({ data }) => {
        if (!data) return;
        const { tournamentGameCategories, tournamentGameIds } = data;
        useCommonStore.setState({
          tournamentGameCategories: tournamentGameCategories
            ? tournamentGameCategories.split(',').map((item) => item.trim())
            : [],
          tournamentGameIds: tournamentGameIds
            ? tournamentGameIds.split(',').map((item) => item.trim())
            : [],
        });
      });
    } catch (error: unknown) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    try {
      if (isAuthenticated) {
        fetchToStore(fetchUserLimits, 'limits');
      }
      if (!isAuthenticated) {
        fetchToStore(
          fetchAuthProvidersOptions,
          'authProvidersOptions',
          'pendingAuthProvidersOptions'
        );
        return;
      }
    } catch (error: unknown) {
      console.log(error);
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    try {
      if (pendingUserPure) return;

      fetchToStore(
        fetchPlayerSettings,
        'playerSettings',
        'pendingPlayerSettings'
      );
      mutate('/info/project');
    } catch (error: unknown) {
      console.log(error);
    }
  }, [pendingUserPure, isAuthenticated, mutate]);

  const isInvreasedPrevDepAB = useLocalStorageStore.getState().get<{
    name: InvreasedPrevDepEnum;
  }>('ab-testing__exp_increased_prev_dep_lu');

  useEffect(() => {
    if (
      !user ||
      groupMemberNoDeposits(user) ||
      currentWallet?.isCrypto ||
      lastSuccessfulPaymentsByCurrency[user.currency] !== undefined ||
      isInvreasedPrevDepAB?.name === InvreasedPrevDepEnum.A
    )
      return;
    useCommonStore.setState({ lastSuccessfulPaymentsByCurrencyPending: true });

    const to = format(new Date(), 'yyyy-MM-dd');
    const from = format(subDays(new Date(), 10), 'yyyy-MM-dd');

    const controller = new AbortController();
    const signal = controller.signal;

    const timeoutId = setTimeout(() => controller.abort(), 5000);

    fetchPlayerPaymentsWithPages(
      {
        page: 1,
        filter: {
          currency: user.currency,
          type: MethodGroupType.DEPOSIT,
          from,
          to,
        },
      },
      signal
    )
      .then(({ data }) => {
        if (!data) return;
        const transaction = data.data.find(
          ({ state, currency }) =>
            state === TransactionState.Succeeded && currency === user.currency
        );
        useCommonStore.setState({
          lastSuccessfulPaymentsByCurrency: {
            ...lastSuccessfulPaymentsByCurrency,
            [user.currency]: transaction?.amountCents || 0,
          },
        });
      })
      .finally(() => {
        useCommonStore.setState({
          lastSuccessfulPaymentsByCurrencyPending: false,
        });
      });

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [user, currentWallet, lastSuccessfulPaymentsByCurrency]);

  return null;
};
